import React, { useState, useEffect } from 'react';
import { Save, Send, Clock, Sparkles, Brain, CheckSquare, BookOpen, Shield, AlertTriangle, Layers } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import EmployeeSelector from './EmployeeSelector';
import QuizBuilder from './QuizBuilder';
import TrainingBuilder from './TrainingBuilder';
import ChecklistBuilder from './ChecklistBuilder';
import CombinedTaskBuilder from './CombinedTaskBuilder';
import UnifiedFilePickerModal from '../Common/UnifiedFilePickerModal';
import DocumentPromptModal from './DocumentPromptModal';
import { FileText, Image, Video, File, X, Paperclip, Eye } from 'lucide-react';
import FileViewer from './FileViewer';
import api from "../../utils/api";

const TaskCreationModal = ({ isOpen, onClose, onSuccess, task, phase, isComplianceMode = false }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        task_type: 'combined',
        content: '',
        phase: phase || 'preboarding',
        category: '',
        priority: 'medium',
        estimated_duration: 30,
        requires_proof: false,
        requires_signature: false,
        assigned_roles: [],
        assigned_employee_ids: [],
        assigned_team_ids: [],
        assignment_metadata: { recursive: false, leaders_only: false },
        assignment_type: 'role',
        passing_score: 70,
        quiz_grading_type: 'auto',
        allow_retakes: false,
        max_attempts: 1,
        due_days_after_assignment: 7,
        scheduled_date: null,
        attachments: [],
        is_compliance_required: isComplianceMode,
        compliance_deadline_days: null,
        is_combined: true,
        sections: []
    });

    // Track if compliance was enabled when task was sent (cannot be unchecked after)
    const [complianceLocked, setComplianceLocked] = useState(false);

    const [showAIPrompt, setShowAIPrompt] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);

    // File Attachment State
    const [showFilePicker, setShowFilePicker] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [showDocumentPromptModal, setShowDocumentPromptModal] = useState(false);
    const [pendingDocumentPromptFiles, setPendingDocumentPromptFiles] = useState([]);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await api.get('/employees/roles');
                setAvailableRoles(response.data.roles);
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        };
        fetchRoles();
    }, []);

    // Handler to change task type and clear content to prevent incompatible data structures
    const handleTaskTypeChange = (newTaskType) => {
        const isCombined = newTaskType === 'combined';
        setFormData(prev => ({
            ...prev,
            task_type: newTaskType,
            content: '', // Clear content when switching task types
            is_combined: isCombined,
            sections: isCombined ? prev.sections : [] // Preserve sections if switching back to combined
        }));
    };

    useEffect(() => {
        if (task) {
            // Format scheduled_date for datetime-local input
            let formattedScheduledDate = null;
            if (task.scheduled_date) {
                const date = new Date(task.scheduled_date);
                // Format to YYYY-MM-DDTHH:MM for datetime-local input
                formattedScheduledDate = date.toISOString().slice(0, 16);
            }

            // Parse attachments if it's a JSON string
            let parsedAttachments = [];
            if (task.attachments) {
                if (typeof task.attachments === 'string') {
                    try {
                        parsedAttachments = JSON.parse(task.attachments);
                    } catch (e) {
                        console.error('Failed to parse attachments:', e);
                        parsedAttachments = [];
                    }
                } else if (Array.isArray(task.attachments)) {
                    parsedAttachments = task.attachments;
                }
            }

            setFormData({
                title: task.title || '',
                description: task.description || '',
                task_type: task.task_type || 'task',
                content: task.content || '',
                phase: task.phase || phase || 'preboarding',
                category: task.category || '',
                priority: task.priority || 'medium',
                estimated_duration: task.estimated_duration || 30,
                requires_proof: task.requires_proof || false,
                requires_signature: task.requires_signature || false,
                assigned_roles: Array.isArray(task.assigned_roles) ? task.assigned_roles : [],
                assigned_employee_ids: Array.isArray(task.assigned_employee_ids) ? task.assigned_employee_ids : [],
                assigned_team_ids: Array.isArray(task.assigned_team_ids) ? task.assigned_team_ids : [],
                assignment_metadata: task.assignment_metadata || { recursive: false, leaders_only: false },
                assignment_type: task.assignment_type || 'role',
                passing_score: task.passing_score || 70,
                quiz_grading_type: task.quiz_grading_type || 'auto',
                allow_retakes: task.allow_retakes || false,
                max_attempts: task.max_attempts || 1,
                due_days_after_assignment: task.due_days_after_assignment || 7,
                scheduled_date: formattedScheduledDate,
                attachments: parsedAttachments,
                is_compliance_required: task.is_compliance_required || isComplianceMode,
                compliance_deadline_days: task.compliance_deadline_days || null,
                is_combined: task.is_combined || (task.task_type === 'combined') || false,
                sections: task.sections || []
            });

            // If it's a combined task but sections are missing (because list endpoint doesn't return them),
            // we need to fetch them
            if ((task.is_combined || task.task_type === 'combined') && (!task.sections || task.sections.length === 0)) {
                const fetchSections = async () => {
                    try {
                        const response = await api.get(`/tasks/${task.id}/sections`);
                        if (response.data.success && response.data.sections) {
                            setFormData(prev => ({
                                ...prev,
                                sections: response.data.sections
                            }));
                        }
                    } catch (error) {
                        console.error('Error fetching task sections:', error);
                    }
                };
                fetchSections();
            }

            // Lock compliance checkbox if task was already published with compliance enabled
            if (task.is_compliance_required && task.status === 'published') {
                setComplianceLocked(true);
            }
        }
    }, [task, phase, isComplianceMode]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRolesChange = (e) => {
        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prev => ({ ...prev, assigned_roles: selectedOptions }));
    };

    /* const handleGenerateWithAI = async () => {
         if (!aiPrompt.trim()) {
             alert('Please enter a prompt for AI generation');
             return;
         }
 
         setIsGenerating(true);
         try {
             const token = localStorage.getItem('token');
             const response = await api.post(
                 '/tasks/generate-ai',
                 {
                     prompt: aiPrompt,
                     phase: formData.phase,
                     task_type: formData.task_type
                 },
                 {
                     headers: { Authorization: `Bearer ${token}` }
                 }
             );
 
             if (response.data.success) {
                 const aiTask = response.data.task;
 
                 // Update form data with AI-generated content
                 setFormData(prev => ({
                     ...prev,
                     title: aiTask.title || prev.title,
                     description: aiTask.description || prev.description,
                     content: aiTask.content || prev.content,
                     task_type: aiTask.task_type || prev.task_type
                 }));
 
                 setShowAIPrompt(false);
                 setAiPrompt('');
 
                 // Show success message based on task type
                 const taskTypeLabel = {
                     'quiz': 'Quiz',
                     'training': 'Training',
                     'checklist': 'Checklist',
                     'task': 'Task'
                 }[aiTask.task_type] || 'Task';
 
                 alert(`${taskTypeLabel} generated with AI! Review and edit as needed, then save or publish.`);
             }
         } catch (error) {
             console.error('AI generation error:', error);
             alert('Failed to generate task with AI');
         } finally {
             setIsGenerating(false);
         }
     }; */

    const handleSave = async (status) => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        setIsSaving(true);
        const loadingToast = toast.loading(task ? 'Updating task...' : 'Creating task...');

        try {
            const token = localStorage.getItem('token');
            const dataToSend = { ...formData, status };

            if (task) {
                // Update existing task
                const response = await api.put(
                    `/tasks/${task.id}`,
                    dataToSend,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                toast.success('Task updated successfully!', { id: loadingToast });
            } else {
                // Create new task
                const response = await api.post(
                    '/tasks',
                    dataToSend,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                toast.success('Task created successfully!', { id: loadingToast });
            }

            onSuccess();
        } catch (error) {
            console.error('Save task error:', error);
            toast.error(error.response?.data?.message || 'Failed to save task', { id: loadingToast });
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublish = async () => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        setIsSaving(true);
        const loadingToast = toast.loading('Publishing task...');

        try {
            const token = localStorage.getItem('token');

            let taskId = task?.id;

            if (!taskId) {
                // Create task first
                const response = await api.post(
                    '/tasks',
                    { ...formData, status: 'draft' },
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                taskId = response.data.task.id;
            } else {
                // Update existing task first
                await api.put(
                    `/tasks/${taskId}`,
                    { ...formData },
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
            }

            // Then publish it
            await api.post(
                `/tasks/${taskId}/publish`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success('Task published successfully!', { id: loadingToast });
            onSuccess();
        } catch (error) {
            console.error('Publish task error:', error);
            toast.error(error.response?.data?.message || 'Failed to publish task', { id: loadingToast });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSchedule = async () => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        if (!formData.scheduled_date) {
            toast.error('Please select a scheduled date');
            return;
        }

        setIsSaving(true);
        const loadingToast = toast.loading('Scheduling task...');

        try {
            const token = localStorage.getItem('token');

            let taskId = task?.id;

            if (!taskId) {
                // Create task first
                const response = await api.post(
                    '/tasks',
                    { ...formData, status: 'draft' },
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                taskId = response.data.task.id;
            } else {
                // Update existing task first
                await api.put(
                    `/tasks/${taskId}`,
                    { ...formData },
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
            }

            // Then schedule it
            await api.post(
                `/tasks/${taskId}/schedule`,
                { scheduled_date: formData.scheduled_date },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success('Task scheduled successfully!', { id: loadingToast });
            onSuccess();
        } catch (error) {
            console.error('Schedule task error:', error);
            toast.error(error.response?.data?.message || 'Failed to schedule task', { id: loadingToast });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4">
            <div className="bg-card rounded-none md:rounded-xl shadow-2xl max-w-[95vw] md:max-w-7xl w-full h-full md:h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3 md:px-6 md:py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-foreground">
                            {task ? 'Edit Task' : 'Create New Task'}
                        </h2>
                        <p className="text-xs md:text-sm text-muted-foreground mt-1">
                            {phase === 'preboarding' ? 'Preboarding' : 'Onboarding'} phase
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground" />
                    </button>
                </div>

                {/* Unified File Picker Modal */}
                <UnifiedFilePickerModal
                    isOpen={showFilePicker}
                    onClose={() => setShowFilePicker(false)}
                    onAttach={(files) => {
                        setPendingDocumentPromptFiles(files);
                        setShowDocumentPromptModal(true);
                        setShowFilePicker(false);
                    }}
                    allowMultiple={true}
                />

                {/* File Viewer */}
                <FileViewer
                    file={previewFile}
                    isOpen={!!previewFile}
                    onClose={() => setPreviewFile(null)}
                />

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6">
                    {/* AI Generation Section */}
                    {/* ... (AI section commented out in original) ... */}



                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Task Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter task title"
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                required
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Provide a brief description"
                                rows="3"
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Task Content
                            </label>

                            {/* Unified Task Builder */}
                            <CombinedTaskBuilder
                                sections={formData.sections}
                                onChange={(sections) => setFormData(prev => ({ ...prev, sections }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Category
                            </label>
                            <input
                                type="text"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                placeholder="e.g., Compliance, IT Setup"
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Priority
                            </label>
                            <select
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Estimated Duration (minutes)
                            </label>
                            <input
                                type="number"
                                name="estimated_duration"
                                value={formData.estimated_duration}
                                onChange={handleChange}
                                min="5"
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Due Days After Assignment
                            </label>
                            <input
                                type="number"
                                name="due_days_after_assignment"
                                value={formData.due_days_after_assignment}
                                onChange={handleChange}
                                min="1"
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {formData.task_type === 'quiz' && (
                            <>
                                <div>
                                    <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                        Passing Score (%)
                                    </label>
                                    <input
                                        type="number"
                                        name="passing_score"
                                        value={formData.passing_score}
                                        onChange={handleChange}
                                        min="0"
                                        max="100"
                                        className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                        Grading Type
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, quiz_grading_type: 'auto' }))}
                                            className={`p-3 md:p-4 border-2 rounded-xl flex flex-col items-center gap-2 transition-all ${formData.quiz_grading_type === 'auto'
                                                ? 'border-green-500 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                                                : 'border-border hover:border-border'
                                                }`}
                                        >
                                            <Brain className="w-5 h-5 md:w-6 md:h-6" />
                                            <span className="font-medium text-xs md:text-sm">Auto-Graded</span>
                                            <span className="text-xs text-muted-foreground text-center hidden md:block">Multiple choice questions graded automatically</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, quiz_grading_type: 'manual' }))}
                                            className={`p-3 md:p-4 border-2 rounded-xl flex flex-col items-center gap-2 transition-all ${formData.quiz_grading_type === 'manual'
                                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400'
                                                : 'border-border hover:border-border'
                                                }`}
                                        >
                                            <FileText className="w-5 h-5 md:w-6 md:h-6" />
                                            <span className="font-medium text-xs md:text-sm">Manual Review</span>
                                            <span className="text-xs text-muted-foreground text-center hidden md:block">Written answers reviewed by admin</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="allow_retakes"
                                            checked={formData.allow_retakes}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                                        />
                                        <span className="text-sm text-muted-foreground">Allow Retakes</span>
                                    </label>
                                    {formData.allow_retakes && (
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm text-muted-foreground">Max Attempts:</label>
                                            <input
                                                type="number"
                                                name="max_attempts"
                                                value={formData.max_attempts}
                                                onChange={handleChange}
                                                min="1"
                                                max="10"
                                                className="w-20 px-3 py-1 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div>
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Schedule Date (optional)
                            </label>
                            <input
                                type="datetime-local"
                                name="scheduled_date"
                                value={formData.scheduled_date}
                                onChange={handleChange}
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Employee Assignment */}
                    <EmployeeSelector
                        phase={formData.phase}
                        selectedEmployees={formData.assigned_employee_ids}
                        onSelectionChange={(ids) => setFormData(prev => ({ ...prev, assigned_employee_ids: ids }))}
                        assignmentType={formData.assignment_type}
                        onAssignmentTypeChange={(type) => setFormData(prev => ({ ...prev, assignment_type: type }))}
                        selectedTeams={formData.assigned_team_ids}
                        onTeamSelectionChange={(ids) => setFormData(prev => ({ ...prev, assigned_team_ids: ids }))}
                        assignmentMetadata={formData.assignment_metadata}
                        onMetadataChange={(meta) => setFormData(prev => ({ ...prev, assignment_metadata: meta }))}
                    />

                    {/* Role Selector (only shown if assignment_type is 'role') */}
                    {formData.assignment_type === 'role' && (
                        <div>
                            <label className="block text-sm font-semibold text-muted-foreground mb-1.5 md:mb-2">
                                Assign to Roles (hold Ctrl/Cmd for multiple)
                            </label>
                            <select
                                multiple
                                value={formData.assigned_roles}
                                onChange={handleRolesChange}
                                className="w-full px-3 py-2 md:px-4 md:py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                size="4"
                            >
                                <option value="">All Roles</option>
                                {availableRoles.map((role) => (
                                    <option key={role.id} value={role.role_name}>
                                        {role.role_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Compliance Section */}
                    {/* (Kept mostly same but checked formatting) */}
                    <div className={`rounded-xl p-3 md:p-4 border-2 transition-all ${formData.is_compliance_required
                        ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200 dark:border-red-500/30'
                        : 'bg-secondary border-border'
                        }`}>
                        <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${formData.is_compliance_required
                                ? 'bg-red-100 dark:bg-red-500/20'
                                : 'bg-muted'
                                }`}>
                                <Shield className={`w-5 h-5 ${formData.is_compliance_required
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-muted-foreground'
                                    }`} />
                            </div>
                            <div className="flex-1">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="is_compliance_required"
                                        checked={formData.is_compliance_required}
                                        onChange={handleChange}
                                        disabled={complianceLocked || isComplianceMode}
                                        className={`w-4 h-4 rounded focus:ring-2 ${formData.is_compliance_required
                                            ? 'text-red-600 dark:text-red-400 focus:ring-red-500'
                                            : 'text-primary focus:ring-primary'
                                            } ${(complianceLocked || isComplianceMode) ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    />
                                    <span className="text-sm font-semibold text-foreground">
                                        This is a compliance-required task
                                    </span>
                                </label>
                                <p className="text-xs text-muted-foreground mt-1 ml-6">
                                    Compliance tasks are legally required and tracked separately.
                                    Overdue tasks create violations and trigger automatic alerts.
                                </p>

                                {formData.is_compliance_required && !task && (
                                    <div className="mt-2 md:mt-3 ml-6 p-2 md:p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <p className="text-xs text-amber-800 dark:text-amber-400">
                                                <strong>Warning:</strong> Once this task is published with compliance enabled,
                                                it cannot be changed to a regular task.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {(complianceLocked || isComplianceMode) && (
                                    <p className="text-xs text-red-600 dark:text-red-400 mt-2 ml-6 font-medium">
                                        {isComplianceMode
                                            ? 'Creating from Compliance dashboard - compliance flag is locked.'
                                            : 'Compliance flag is locked for published tasks.'
                                        }
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>



                    {/* File Attachments Section */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="block text-sm font-semibold text-muted-foreground">
                                File Attachments (Optional)
                            </label>
                            <button
                                type="button"
                                onClick={() => setShowFilePicker(true)}
                                className="text-sm text-primary hover:text-primary-hover font-medium flex items-center gap-1"
                            >
                                <Paperclip className="w-4 h-4" />
                                Attach Files
                            </button>
                        </div>

                        {/* Attached Files List */}
                        {formData.attachments.length > 0 ? (
                            <div className="grid grid-cols-1 gap-2">
                                {formData.attachments.map((file, index) => {
                                    const getFileIcon = (fileType) => {
                                        if (!fileType) return <File className="w-5 h-5 text-muted-foreground" />;
                                        if (fileType.startsWith('image/')) return <Image className="w-5 h-5 text-purple-600" />;
                                        if (fileType === 'application/pdf') return <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />;
                                        if (fileType.startsWith('video/')) return <Video className="w-5 h-5 text-blue-600" />;
                                        return <File className="w-5 h-5 text-muted-foreground" />;
                                    };

                                    return (
                                        <div key={index} className="flex items-center justify-between p-3 bg-secondary border border-border rounded-lg group">
                                            <div
                                                className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                                                onClick={() => setPreviewFile(file)}
                                            >
                                                {getFileIcon(file.file_type || file.type)}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-foreground truncate">{file.file_name || file.name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {file.file_size ? `${(file.file_size / 1024).toFixed(0)} KB` : 'Unknown size'}
                                                        {file.source === 'library' && ' • From Library'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewFile(file)}
                                                    className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded transition-colors"
                                                    title="Preview"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newAttachments = [...formData.attachments];
                                                        newAttachments.splice(index, 1);
                                                        setFormData(prev => ({ ...prev, attachments: newAttachments }));
                                                    }}
                                                    className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:bg-red-500/10 rounded transition-colors"
                                                    title="Remove"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div
                                onClick={() => setShowFilePicker(true)}
                                className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center text-muted-foreground hover:bg-secondary hover:border-muted transition-all cursor-pointer"
                            >
                                <div className="p-3 bg-gray-100 dark:bg-slate-800 rounded-full mb-3">
                                    <Paperclip className="w-6 h-6 text-muted-foreground" />
                                </div>
                                <p className="text-sm font-medium">No files attached</p>
                                <p className="text-xs mt-1">Click to attach files from computer or library</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex-shrink-0 bg-secondary border-t border-border px-4 py-3 md:px-6 md:py-4 flex flex-col md:flex-row gap-2 md:gap-3 justify-end">
                    <button
                        onClick={onClose}
                        className="w-full md:w-auto px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-muted transition-colors order-2 md:order-1"
                    >
                        Cancel
                    </button>
                    <div className="flex flex-col md:flex-row gap-2 md:gap-3 order-1 md:order-2">
                        <button
                            onClick={() => handleSave('draft')}
                            disabled={isSaving}
                            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            <span className="md:hidden lg:inline">Save Draft</span>
                            <span className="hidden md:inline lg:hidden">Draft</span>
                        </button>
                        <button
                            onClick={handleSchedule}
                            disabled={isSaving || !formData.scheduled_date}
                            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <Clock className="w-4 h-4" />
                            Schedule
                        </button>
                        <button
                            onClick={handlePublish}
                            disabled={isSaving}
                            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <Send className="w-4 h-4" />
                            <span className="md:hidden lg:inline">Publish Now</span>
                            <span className="hidden md:inline lg:hidden">Publish</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Document Prompt Modal */}
            <DocumentPromptModal
                isOpen={showDocumentPromptModal}
                onClose={() => {
                    setFormData(prev => ({
                        ...prev,
                        attachments: [...prev.attachments, ...pendingDocumentPromptFiles.map(f => ({ ...f, add_to_documents: false }))]
                    }));
                    toast.success(`Attached ${pendingDocumentPromptFiles.length} file(s)`);
                    setShowDocumentPromptModal(false);
                    setPendingDocumentPromptFiles([]);
                }}
                onConfirm={(addToDocuments) => {
                    setFormData(prev => ({
                        ...prev,
                        attachments: [...prev.attachments, ...pendingDocumentPromptFiles.map(f => ({ ...f, add_to_documents: addToDocuments }))]
                    }));
                    toast.success(`Attached ${pendingDocumentPromptFiles.length} file(s)`);
                    setShowDocumentPromptModal(false);
                    setPendingDocumentPromptFiles([]);
                }}
                fileCount={pendingDocumentPromptFiles.length}
            />
        </div>
    );
};

export default TaskCreationModal;

